// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { X, Plus, Settings } from "lucide-react";
import { productService } from "../../../services/productService";
import MultiSelectFilter from "./MultiSelectFilter";
import {
  SCENARIO_OPTIONS,
  PACKAGE_OPTIONS,
  LAYOUT_POSITION_OPTIONS,
  MIETDAUER_OPTIONS,
} from "../../../utils/scenarioConfig";

const AddProductForm = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    vhp_name: "",
    vhp_sku: "",
    vhp_product_type: "",
    type: "other",
    description: "",
    html_description: "",
    vhp_location: "1",
    vhp_archiving_flag: "active",
    bruttopreis_pro_einheit: "",
    vhp_net_price: "",
    unit: "",
    mindestabnahme: {
      default: 0,
      configurations: [],
    },
    maximalmenge: {
      default: 0,
      configurations: [],
    },
    mietdauer: "einmalig",
    applicable_for: "both",
    layout_position: "Hidden",
    paket: [],
    bookable_for_scenarios: [],
    bookable_for_original: "",
    external_id: "",
    icon_asset: "",
    vhp_image_url: "",
    vhp_image_caption: "",
    vhp_cost_of_goods_sold: "",
    ek_miete_netto: "",
    ek_miete_brutto: "",
    cost_anlieferung_abholung: "",
    cost_aufbau_abbau: "",
    quelle_miete_url: "",
    ek_invest_netto: "",
    quelle_invest_url: "",
    height: "",
    width: "",
    depth: "",
    aufbauzeit_h: "",
    abbauzeit_h: "",
    gps_lat: "",
    gps_lng: "",
    orientation: "",
    price_factor1: "1",
    price_factor2: "1",
    price_factor3: "1",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [_submitError, setSubmitError] = useState(null);

  // Refs for required fields (focus/scroll on validation error)
  const fieldRefs = useRef({
    vhp_name: null,
    vhp_sku: null,
    vhp_product_type: null,
    type: null,
    layout_position: null,
    vhp_location: null,
    bruttopreis_pro_einheit: null,
    vhp_net_price: null,
  });

  // Dynamic data states
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [scenarioDropdownOpen, setScenarioDropdownOpen] = useState(false);
  const [productTypes, setProductTypes] = useState([]);
  const [loadingProductTypes, setLoadingProductTypes] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [applicableForOptions, setApplicableForOptions] = useState([]);
  const [loadingApplicableFor, setLoadingApplicableFor] = useState(false);
  const [packageOptions, setPackageOptions] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [packageDropdownOpen, setPackageDropdownOpen] = useState(false);

  // Configuration modal states
  const [minConfigModalOpen, setMinConfigModalOpen] = useState(false);
  const [maxConfigModalOpen, setMaxConfigModalOpen] = useState(false);
  const [minConfigurations, setMinConfigurations] = useState([]);
  const [maxConfigurations, setMaxConfigurations] = useState([]);
  const [minDefault, setMinDefault] = useState(0);
  const [maxDefault, setMaxDefault] = useState(0);

  // Fetch all dynamic data when component mounts
  useEffect(() => {
    const fetchData = async () => {
      setLoadingLocations(true);
      setLoadingScenarios(true);
      setLoadingProductTypes(true);
      setLoadingCategories(true);
      setLoadingStatus(true);
      setLoadingApplicableFor(true);
      setLoadingPackages(true);

      try {
        const filtersData = await productService.getAllFilters();
        setLocations(filtersData.locations || []);

        const scenarioOptions = (filtersData.scenarios || []).map(
          (scenario) => ({
            value: scenario.scenario_code || scenario.name,
            label: scenario.name || scenario.scenario_code || "Unknown",
          }),
        );
        setScenarios(scenarioOptions);

        // Process product types for dropdown
        const productTypeOptions = (filtersData.productTypes || []).map(
          (type) => ({
            value: type.id,
            label: type.name,
          }),
        );
        setProductTypes(productTypeOptions);

        // Process categories for dropdown
        const categoryOptions = (filtersData.categories || []).map(
          (category) => ({
            value: category.id,
            label: category.name,
          }),
        );
        setCategories(categoryOptions);

        // Process status options for dropdown
        const statusOptionsData = (filtersData.status || []).map((status) => ({
          value: status.id,
          label: status.name,
        }));
        setStatusOptions(statusOptionsData);

        // Process applicable for options
        const applicableForOptionsData = (
          filtersData.applicableForOptions || []
        ).map((option) => ({
          value: option.id,
          label: option.name,
        }));
        setApplicableForOptions(applicableForOptionsData);

        // Process package options for multi-select; API enum is S,M,L only.
        // Exclude "none" from API if present, then add single "None (no package)" (API receives []).
        const apiPaket = filtersData.paketOptions || [];
        const packageOptionsData = apiPaket
          .filter((pkg) => pkg !== "none")
          .map((pkg) => ({ value: pkg, label: pkg }));
        setPackageOptions([
          ...packageOptionsData,
          { value: "none", label: "None (no package)" },
        ]);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLocations([]);
        setScenarios([]);
        setProductTypes([]);
        setCategories([]);
        setStatusOptions([]);
        setApplicableForOptions([]);
        setPackageOptions([]);
      } finally {
        setLoadingLocations(false);
        setLoadingScenarios(false);
        setLoadingProductTypes(false);
        setLoadingCategories(false);
        setLoadingStatus(false);
        setLoadingApplicableFor(false);
        setLoadingPackages(false);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    // Auto-calculate gross price (brutto) from net when net changes
    if (field === "vhp_net_price") {
      const net = parseFloat(value);
      const gross = isNaN(net) ? "" : (net * 1.19).toFixed(2);
      setFormData((prev) => ({
        ...prev,
        vhp_net_price: value,
        bruttopreis_pro_einheit: gross,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleScenarioSelectionChange = (selectedValues) => {
    handleInputChange("bookable_for_scenarios", selectedValues);
  };

  const handleScenarioDropdownToggle = (_filterKey) => {
    setScenarioDropdownOpen(!scenarioDropdownOpen);
  };

  const handlePackageSelectionChange = (selectedValues) => {
    handleInputChange("paket", selectedValues);
  };

  const handlePackageDropdownToggle = (_filterKey) => {
    setPackageDropdownOpen(!packageDropdownOpen);
  };

  const validateForm = () => {
    const newErrors = {};
    let firstErrorKey = null;

    const setError = (key, message) => {
      newErrors[key] = message;
      if (firstErrorKey === null) firstErrorKey = key;
    };

    if (!formData.vhp_name.trim()) {
      setError("vhp_name", "Name ist erforderlich");
    }

    if (!formData.vhp_sku) {
      setError("vhp_sku", "SKU ist erforderlich");
    } else {
      const skuValue = parseInt(formData.vhp_sku);
      if (skuValue < 10000) {
        setError("vhp_sku", "SKU muss größer oder gleich 10000 sein");
      } else if (skuValue > 80999) {
        setError("vhp_sku", "SKU muss kleiner oder gleich 80999 sein");
      }
    }

    if (!formData.vhp_product_type) {
      setError("vhp_product_type", "Produkttyp ist erforderlich");
    }

    if (!formData.type) {
      setError("type", "Typ ist erforderlich");
    }

    if (!formData.layout_position) {
      setError("layout_position", "Layout Position ist erforderlich");
    } else if (
      formData.layout_position === "Pflicht" &&
      (Number(formData.mindestabnahme?.default) || 0) < 1
    ) {
      // Mandatory (Pflicht) items must never be zero — mirror the API rule.
      setError(
        "mindestabnahme",
        "Pflicht-Artikel müssen eine Mindestabnahme von mindestens 1 haben",
      );
    }

    if (!formData.vhp_location) {
      setError("vhp_location", "Standort ist erforderlich");
    }

    if (
      formData.bruttopreis_pro_einheit &&
      isNaN(parseFloat(formData.bruttopreis_pro_einheit))
    ) {
      setError(
        "bruttopreis_pro_einheit",
        "Bitte geben Sie einen gültigen Preis ein",
      );
    }

    if (formData.vhp_net_price === "" || formData.vhp_net_price === null) {
      setError("vhp_net_price", "Nettopreis ist erforderlich");
    } else if (isNaN(parseFloat(formData.vhp_net_price))) {
      setError("vhp_net_price", "Bitte geben Sie einen gültigen Preis ein");
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    return { isValid, firstErrorKey };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { isValid, firstErrorKey } = validateForm();
    if (!isValid) {
      // Focus and scroll to first invalid required field
      setTimeout(() => {
        const el = fieldRefs.current[firstErrorKey];
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.focus();
        }
      }, 0);
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      // When package is [] or "none" selected, send empty array (delivery products have no package)
      const paketForApi =
        !formData.paket?.length || formData.paket.includes("none")
          ? []
          : (formData.paket || []).filter((p) => p !== "none");

      // Drop incomplete quantity-config rows before sending. A row is only
      // meaningful when both scenario and package are chosen; otherwise the
      // user added a row but never filled it in.
      const cleanQuantityConfig = (field) => {
        const value = formData[field];
        const configs = Array.isArray(value?.configurations)
          ? value.configurations.filter((c) => c.scenario && c.package)
          : [];
        return {
          default: Number(value?.default) || 0,
          configurations: configs.map((c) => ({
            scenario: c.scenario,
            package: c.package,
            quantity: Number(c.quantity) || 0,
          })),
        };
      };

      // Prepare data for API
      const productData = {
        ...formData,
        paket: paketForApi,
        mindestabnahme: cleanQuantityConfig("mindestabnahme"),
        maximalmenge: cleanQuantityConfig("maximalmenge"),
        bruttopreis_pro_einheit: formData.bruttopreis_pro_einheit
          ? parseFloat(formData.bruttopreis_pro_einheit)
          : 0,
        vhp_net_price: formData.vhp_net_price
          ? parseFloat(formData.vhp_net_price)
          : 0,
        vhp_cost_of_goods_sold: formData.vhp_cost_of_goods_sold
          ? parseFloat(formData.vhp_cost_of_goods_sold)
          : 0,
        ek_miete_netto: formData.ek_miete_netto
          ? parseFloat(formData.ek_miete_netto)
          : 0,
        ek_miete_brutto: formData.ek_miete_brutto
          ? parseFloat(formData.ek_miete_brutto)
          : 0,
        cost_anlieferung_abholung: formData.cost_anlieferung_abholung
          ? parseFloat(formData.cost_anlieferung_abholung)
          : 0,
        cost_aufbau_abbau: formData.cost_aufbau_abbau
          ? parseFloat(formData.cost_aufbau_abbau)
          : 0,
        ek_invest_netto: formData.ek_invest_netto
          ? parseFloat(formData.ek_invest_netto)
          : 0,
        height: formData.height ? parseFloat(formData.height) : 0,
        width: formData.width ? parseFloat(formData.width) : 0,
        depth: formData.depth ? parseFloat(formData.depth) : 0,
        gps_lat: formData.gps_lat ? parseFloat(formData.gps_lat) : 0,
        gps_lng: formData.gps_lng ? parseFloat(formData.gps_lng) : 0,
        orientation: formData.orientation
          ? parseFloat(formData.orientation)
          : 0,
        aufbauzeit_h: formData.aufbauzeit_h
          ? parseFloat(formData.aufbauzeit_h)
          : 0,
        abbauzeit_h: formData.abbauzeit_h
          ? parseFloat(formData.abbauzeit_h)
          : 0,
        created_at: new Date().toISOString(),
        vhp_last_modified: new Date().toISOString(),
      };

      await onSave(productData);
      handleClose();
    } catch (error) {
      console.error("Error creating product:", error);
      // Show API error above form; keep form open
      const data = error?.response?.data ?? error?.data ?? {};
      const detail = data.detail;
      const message =
        data.message ||
        (typeof detail === "string"
          ? detail
          : detail && typeof detail === "object" && detail.message
            ? detail.message
            : Array.isArray(detail)
              ? detail
                  .map((d) => d.msg ?? d.message ?? JSON.stringify(d))
                  .join(" ")
              : typeof detail === "object" && detail !== null
                ? (detail.message ?? JSON.stringify(detail))
                : undefined) ||
        error?.message ||
        "Fehler beim Speichern des Produkts.";
      toast.error(String(message ?? "Fehler beim Speichern des Produkts."), {
        duration: 6000,
      });
      setSubmitError(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      vhp_name: "",
      vhp_sku: "",
      vhp_product_type: "",
      type: "other",
      description: "",
      html_description: "",
      vhp_location: "1",
      vhp_archiving_flag: "active",
      bruttopreis_pro_einheit: "",
      vhp_net_price: "",
      unit: "",
      mindestabnahme: {
        default: 0,
        configurations: [],
      },
      maximalmenge: {
        default: 0,
        configurations: [],
      },
      mietdauer: "einmalig",
      applicable_for: "both",
      layout_position: "Hidden",
      paket: [],
      bookable_for_scenarios: [],
      bookable_for_original: "",
      external_id: "",
      icon_asset: "",
      vhp_image_url: "",
      vhp_image_caption: "",
      vhp_cost_of_goods_sold: "",
      ek_miete_netto: "",
      ek_miete_brutto: "",
      cost_anlieferung_abholung: "",
      cost_aufbau_abbau: "",
      quelle_miete_url: "",
      ek_invest_netto: "",
      quelle_invest_url: "",
      height: "",
      width: "",
      depth: "",
      aufbauzeit_h: "",
      abbauzeit_h: "",
      gps_lat: "",
      gps_lng: "",
      orientation: "",
      price_factor1: "1",
      price_factor2: "1",
      price_factor3: "1",
    });
    setErrors({});
    setSubmitError(null);
    setIsSubmitting(false);
    onClose();
  };

  // Configuration modal handlers
  const handleMinConfigOpen = () => {
    // Use `??` so 0 is preserved (unlike `||` which would coerce it to the fallback)
    setMinDefault(formData.mindestabnahme.default ?? 0);
    setMinConfigurations(formData.mindestabnahme.configurations || []);
    setMinConfigModalOpen(true);
  };

  const handleMaxConfigOpen = () => {
    setMaxDefault(formData.maximalmenge.default ?? 0);
    setMaxConfigurations(formData.maximalmenge.configurations || []);
    setMaxConfigModalOpen(true);
  };

  const handleMinConfigSave = () => {
    const configData = {
      default: minDefault,
      configurations: minConfigurations,
    };
    handleInputChange("mindestabnahme", configData);
    setMinConfigModalOpen(false);
  };

  const handleMaxConfigSave = () => {
    const configData = {
      default: maxDefault,
      configurations: maxConfigurations,
    };
    handleInputChange("maximalmenge", configData);
    setMaxConfigModalOpen(false);
  };

  const addMinConfiguration = () => {
    setMinConfigurations([
      ...minConfigurations,
      {
        scenario: "",
        package: "",
        quantity: 1,
      },
    ]);
  };

  const addMaxConfiguration = () => {
    setMaxConfigurations([
      ...maxConfigurations,
      {
        scenario: "",
        package: "",
        quantity: 1,
      },
    ]);
  };

  const removeMinConfiguration = (index) => {
    if (minConfigurations.length > 1) {
      setMinConfigurations(minConfigurations.filter((_, i) => i !== index));
    }
  };

  const removeMaxConfiguration = (index) => {
    if (maxConfigurations.length > 1) {
      setMaxConfigurations(maxConfigurations.filter((_, i) => i !== index));
    }
  };

  const updateMinConfiguration = (index, field, value) => {
    const updated = [...minConfigurations];
    updated[index][field] = value;
    setMinConfigurations(updated);
  };

  const updateMaxConfiguration = (index, field, value) => {
    const updated = [...maxConfigurations];
    updated[index][field] = value;
    setMaxConfigurations(updated);
  };

  const getConfigurationSummary = (configurations) => {
    if (!configurations || configurations.length === 0) {
      return "Keine Konfiguration";
    }
    return `${configurations.length}`;
  };

  // Using centralized scenario and package options from scenarioConfig

  return (
    <div className="absolute right-0 top-0 h-full z-50">
      <div className="relative bg-white shadow-xl w-full max-w-xl h-full overflow-hidden">
        {/* Overlay + loader when submitting */}
        {isSubmitting && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/80 backdrop-blur-[2px]">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600" />
              <p className="text-sm font-medium text-gray-700">
                Produkt wird hinzugefügt...
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex justify-between items-center px-3 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="text-xl font-semibold text-gray-900">
            Neues Produkt hinzufügen
          </h3>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2"
            disabled={isSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="p-6 space-y-6 overflow-y-auto max-h-[calc(100vh-61px)]"
          style={{ scrollbarWidth: "thin" }}
        >
          {/* Basic Information */}
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-4">
              Grundinformationen
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  ref={(el) => (fieldRefs.current.vhp_name = el)}
                  type="text"
                  value={formData.vhp_name}
                  onChange={(e) =>
                    handleInputChange("vhp_name", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.vhp_name ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="Produktname eingeben"
                  disabled={isSubmitting}
                />
                {errors.vhp_name && (
                  <p className="text-red-500 text-xs mt-1">{errors.vhp_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SKU *
                </label>
                <input
                  ref={(el) => (fieldRefs.current.vhp_sku = el)}
                  type="number"
                  value={formData.vhp_sku}
                  onChange={(e) => handleInputChange("vhp_sku", e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.vhp_sku ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="SKU eingeben"
                  disabled={isSubmitting}
                />
                {errors.vhp_sku && (
                  <p className="text-red-500 text-xs mt-1">{errors.vhp_sku}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Produkttyp *
                </label>
                <select
                  ref={(el) => (fieldRefs.current.vhp_product_type = el)}
                  value={formData.vhp_product_type}
                  onChange={(e) =>
                    handleInputChange("vhp_product_type", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.vhp_product_type
                      ? "border-red-300"
                      : "border-gray-300"
                  }`}
                  disabled={isSubmitting || loadingProductTypes}
                >
                  <option value="">Produkttyp auswählen</option>
                  {productTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                {loadingProductTypes && (
                  <p className="text-sm text-gray-500 mt-1">
                    Loading product types...
                  </p>
                )}
                {errors.vhp_product_type && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.vhp_product_type}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Typ *
                </label>
                <select
                  ref={(el) => (fieldRefs.current.type = el)}
                  value={formData.type}
                  onChange={(e) => handleInputChange("type", e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.type ? "border-red-300" : "border-gray-300"
                  }`}
                  disabled={isSubmitting || loadingCategories}
                >
                  <option value="">Typ auswählen</option>
                  {categories.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
                {loadingCategories && (
                  <p className="text-sm text-gray-500 mt-1">
                    Loading categories...
                  </p>
                )}
                {errors.type && (
                  <p className="text-red-500 text-xs mt-1">{errors.type}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beschreibung
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Produktbeschreibung eingeben"
                  disabled={isSubmitting}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  HTML Beschreibung
                </label>
                <textarea
                  value={formData.html_description}
                  onChange={(e) =>
                    handleInputChange("html_description", e.target.value)
                  }
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="HTML Beschreibung eingeben"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          {/* Location and Status */}
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-4">
              Standort & Status
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Standort *
                </label>
                <select
                  ref={(el) => (fieldRefs.current.vhp_location = el)}
                  value={formData.vhp_location}
                  onChange={(e) =>
                    handleInputChange("vhp_location", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.vhp_location ? "border-red-300" : "border-gray-300"
                  }`}
                  disabled={isSubmitting || loadingLocations}
                >
                  <option value="">Standort auswählen</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name} ({location.vhp_code})
                    </option>
                  ))}
                </select>
                {loadingLocations && (
                  <p className="text-sm text-gray-500 mt-1">
                    Loading locations...
                  </p>
                )}
                {errors.vhp_location && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.vhp_location}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.vhp_archiving_flag}
                  onChange={(e) =>
                    handleInputChange("vhp_archiving_flag", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isSubmitting || loadingStatus}
                >
                  <option value="">Status auswählen</option>
                  {statusOptions.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
                {loadingStatus && (
                  <p className="text-sm text-gray-500 mt-1">
                    Loading status...
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mietdauer
                </label>
                <select
                  value={formData.mietdauer}
                  onChange={(e) =>
                    handleInputChange("mietdauer", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isSubmitting}
                >
                  {MIETDAUER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Anwendbar für
                </label>
                <select
                  value={formData.applicable_for}
                  onChange={(e) =>
                    handleInputChange("applicable_for", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isSubmitting || loadingApplicableFor}
                >
                  {applicableForOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {loadingApplicableFor && (
                  <p className="text-sm text-gray-500 mt-1">
                    Loading options...
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-4">Preise</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nettopreis (€)
                </label>
                <input
                  ref={(el) => (fieldRefs.current.vhp_net_price = el)}
                  type="number"
                  step="0.01"
                  value={formData.vhp_net_price}
                  onChange={(e) =>
                    handleInputChange("vhp_net_price", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.vhp_net_price ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="0.00"
                />
                {errors.vhp_net_price && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.vhp_net_price}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bruttopreis (€)
                </label>
                <input
                  ref={(el) => (fieldRefs.current.bruttopreis_pro_einheit = el)}
                  type="number"
                  step="0.01"
                  disabled={true}
                  value={formData.bruttopreis_pro_einheit}
                  onChange={(e) =>
                    handleInputChange("bruttopreis_pro_einheit", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.bruttopreis_pro_einheit
                      ? "border-red-300"
                      : "border-gray-300"
                  }`}
                  placeholder="0.00"
                />
                {errors.bruttopreis_pro_einheit && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.bruttopreis_pro_einheit}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Einheit
                </label>
                <input
                  type="text"
                  value={formData.unit}
                  onChange={(e) => handleInputChange("unit", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="z.B. Stück, Mal, Service"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cost of Goods Sold (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.vhp_cost_of_goods_sold}
                  onChange={(e) =>
                    handleInputChange("vhp_cost_of_goods_sold", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Net Rental Cost (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.ek_miete_netto}
                  onChange={(e) =>
                    handleInputChange("ek_miete_netto", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gross Rental Cost (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.ek_miete_brutto}
                  onChange={(e) =>
                    handleInputChange("ek_miete_brutto", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Net Investment Cost (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.ek_invest_netto}
                  onChange={(e) =>
                    handleInputChange("ek_invest_netto", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delivery/Pickup Cost (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.cost_anlieferung_abholung}
                  onChange={(e) =>
                    handleInputChange(
                      "cost_anlieferung_abholung",
                      e.target.value,
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Setup/Teardown Cost (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.cost_aufbau_abbau}
                  onChange={(e) =>
                    handleInputChange("cost_aufbau_abbau", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price Factor 1
                </label>
                <input
                  type="text"
                  value={formData.price_factor1}
                  onChange={(e) =>
                    handleInputChange("price_factor1", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price Factor 2
                </label>
                <input
                  type="text"
                  value={formData.price_factor2}
                  onChange={(e) =>
                    handleInputChange("price_factor2", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price Factor 3
                </label>
                <input
                  type="text"
                  value={formData.price_factor3}
                  onChange={(e) =>
                    handleInputChange("price_factor3", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          {/* Inventory */}
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-4">
              Lager & Mengen
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mindestabnahme
                </label>
                <div className="flex items-center gap-3">
                  {formData.mindestabnahme &&
                    typeof formData.mindestabnahme === "object" &&
                    formData.mindestabnahme.configurations &&
                    formData.mindestabnahme.configurations.length > 0 && (
                      <span className="text-lg font-medium text-gray-900">
                        {getConfigurationSummary(
                          formData.mindestabnahme.configurations,
                        )}
                      </span>
                    )}
                  <button
                    type="button"
                    onClick={handleMinConfigOpen}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <Settings className="w-4 h-4 text-gray-600" />
                    <span className="text-sm text-gray-700">Konfigurieren</span>
                  </button>
                </div>
                {errors.mindestabnahme && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.mindestabnahme}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maximalmenge
                </label>
                <div className="flex items-center gap-3">
                  {formData.maximalmenge &&
                    typeof formData.maximalmenge === "object" &&
                    formData.maximalmenge.configurations &&
                    formData.maximalmenge.configurations.length > 0 && (
                      <span className="text-lg font-medium text-gray-900">
                        {getConfigurationSummary(
                          formData.maximalmenge.configurations,
                        )}
                      </span>
                    )}
                  <button
                    type="button"
                    onClick={handleMaxConfigOpen}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <Settings className="w-4 h-4 text-gray-600" />
                    <span className="text-sm text-gray-700">Konfigurieren</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Setup Time (hours)
                </label>
                <input
                  type="text"
                  value={formData.aufbauzeit_h}
                  onChange={(e) =>
                    handleInputChange("aufbauzeit_h", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 2.5"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teardown Time (hours)
                </label>
                <input
                  type="text"
                  value={formData.abbauzeit_h}
                  onChange={(e) =>
                    handleInputChange("abbauzeit_h", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 1.5"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          {/* Physical Properties */}
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-4">
              Physische Eigenschaften
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Höhe (cm)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.height}
                  onChange={(e) => handleInputChange("height", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="cm"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Breite (cm)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.width}
                  onChange={(e) => handleInputChange("width", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="cm"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tiefe (cm)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.depth}
                  onChange={(e) => handleInputChange("depth", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="cm"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GPS Latitude
                </label>
                <input
                  type="number"
                  step="0.000001"
                  value={formData.gps_lat}
                  onChange={(e) => handleInputChange("gps_lat", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GPS Longitude
                </label>
                <input
                  type="number"
                  step="0.000001"
                  value={formData.gps_lng}
                  onChange={(e) => handleInputChange("gps_lng", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Orientation (degrees)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.orientation}
                  onChange={(e) =>
                    handleInputChange("orientation", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="degrees"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          {/* Booking & Display Settings */}
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-4">
              Buchungs- & Anzeigeeinstellungen
            </h4>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Buchbare Szenarien
                </label>
                <MultiSelectFilter
                  label="Select Scenarios"
                  options={scenarios}
                  selectedValues={
                    Array.isArray(formData.bookable_for_scenarios)
                      ? formData.bookable_for_scenarios
                      : []
                  }
                  onSelectionChange={handleScenarioSelectionChange}
                  searchPlaceholder="Search scenarios..."
                  isOpen={scenarioDropdownOpen}
                  onToggle={handleScenarioDropdownToggle}
                  filterKey="scenarios"
                />
                {loadingScenarios && (
                  <p className="text-sm text-gray-500 mt-1">
                    Loading scenarios...
                  </p>
                )}
                {errors.bookable_for_scenarios && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.bookable_for_scenarios}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bookable For Original
                </label>
                <input
                  type="text"
                  value={formData.bookable_for_original}
                  onChange={(e) =>
                    handleInputChange("bookable_for_original", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Original bookable scenarios"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Layout Position *
                </label>
                <select
                  ref={(el) => (fieldRefs.current.layout_position = el)}
                  value={formData.layout_position}
                  onChange={(e) =>
                    handleInputChange("layout_position", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.layout_position
                      ? "border-red-300"
                      : "border-gray-300"
                  }`}
                  disabled={isSubmitting}
                >
                  <option value="">Layout Position auswählen</option>
                  {LAYOUT_POSITION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {errors.layout_position && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.layout_position}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Package
                </label>
                <MultiSelectFilter
                  label="Select Packages"
                  options={packageOptions}
                  selectedValues={
                    Array.isArray(formData.paket) && formData.paket.length === 0
                      ? ["none"]
                      : formData.paket || []
                  }
                  onSelectionChange={handlePackageSelectionChange}
                  searchPlaceholder="Search packages..."
                  isOpen={packageDropdownOpen}
                  onToggle={handlePackageDropdownToggle}
                  filterKey="packages"
                />
                {loadingPackages && (
                  <p className="text-sm text-gray-500 mt-1">
                    Loading packages...
                  </p>
                )}
                {errors.paket && (
                  <p className="text-red-500 text-xs mt-1">{errors.paket}</p>
                )}
              </div>
            </div>
          </div>
          {/* External References */}
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-4">
              Externe Referenzen
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  External ID
                </label>
                <input
                  type="text"
                  value={formData.external_id}
                  onChange={(e) =>
                    handleInputChange("external_id", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="External system ID"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Icon Asset
                </label>
                <input
                  type="text"
                  value={formData.icon_asset}
                  onChange={(e) =>
                    handleInputChange("icon_asset", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Icon asset reference"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image URL
                </label>
                <input
                  type="url"
                  value={formData.vhp_image_url}
                  onChange={(e) =>
                    handleInputChange("vhp_image_url", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://example.com/image.jpg"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image Caption
                </label>
                <input
                  type="text"
                  value={formData.vhp_image_caption}
                  onChange={(e) =>
                    handleInputChange("vhp_image_caption", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Image caption"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rental Source URL
                </label>
                <input
                  type="url"
                  value={formData.quelle_miete_url}
                  onChange={(e) =>
                    handleInputChange("quelle_miete_url", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://example.com/rental-source"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Investment Source URL
                </label>
                <input
                  type="url"
                  value={formData.quelle_invest_url}
                  onChange={(e) =>
                    handleInputChange("quelle_invest_url", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://example.com/investment-source"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Wird erstellt...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Produkt hinzufügen
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Min Configuration Modal */}
      {minConfigModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Mindestabnahme - Szenario-Paket-Konfiguration
              </h3>
              <button
                onClick={() => setMinConfigModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Standardwert
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Gilt für alle Szenarien und Pakete, sofern unten keine
                  spezifische Konfiguration hinterlegt ist.
                </p>
                <input
                  type="number"
                  min="0"
                  value={minDefault}
                  onChange={(e) =>
                    setMinDefault(parseInt(e.target.value, 10) || 0)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="space-y-3">
                {/* Configurations */}
                {minConfigurations.map((config, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg"
                  >
                    <select
                      value={config.scenario}
                      onChange={(e) =>
                        updateMinConfiguration(
                          index,
                          "scenario",
                          e.target.value,
                        )
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Szenario wählen</option>
                      {SCENARIO_OPTIONS.map((scenario) => (
                        <option key={scenario.value} value={scenario.value}>
                          {scenario.label}
                        </option>
                      ))}
                    </select>

                    <select
                      value={config.package}
                      onChange={(e) =>
                        updateMinConfiguration(index, "package", e.target.value)
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Paket wählen</option>
                      {PACKAGE_OPTIONS.map((pkg) => (
                        <option key={pkg.value} value={pkg.value}>
                          {pkg.label}
                        </option>
                      ))}
                    </select>

                    <input
                      type="number"
                      min="0"
                      value={config.quantity}
                      onChange={(e) =>
                        updateMinConfiguration(
                          index,
                          "quantity",
                          parseInt(e.target.value) || 0,
                        )
                      }
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />

                    <button
                      onClick={() => removeMinConfiguration(index)}
                      disabled={minConfigurations.length === 1}
                      className={`p-2 rounded-lg transition-colors ${
                        minConfigurations.length === 1
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-red-600 hover:text-red-800 hover:bg-red-50"
                      }`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                {/* Add Configuration Button */}
                <button
                  onClick={addMinConfiguration}
                  className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  <span className="text-lg">+</span>
                  Konfiguration hinzufügen
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
              <button
                onClick={() => setMinConfigModalOpen(false)}
                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleMinConfigSave}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Max Configuration Modal */}
      {maxConfigModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Maximalmenge - Szenario-Paket-Konfiguration
              </h3>
              <button
                onClick={() => setMaxConfigModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Standardwert
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Gilt für alle Szenarien und Pakete, sofern unten keine
                  spezifische Konfiguration hinterlegt ist.
                </p>
                <input
                  type="number"
                  min="0"
                  value={maxDefault}
                  onChange={(e) =>
                    setMaxDefault(parseInt(e.target.value, 10) || 0)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="space-y-3">
                {/* Configurations */}
                {maxConfigurations.map((config, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg"
                  >
                    <select
                      value={config.scenario}
                      onChange={(e) =>
                        updateMaxConfiguration(
                          index,
                          "scenario",
                          e.target.value,
                        )
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Szenario wählen</option>
                      {SCENARIO_OPTIONS.map((scenario) => (
                        <option key={scenario.value} value={scenario.value}>
                          {scenario.label}
                        </option>
                      ))}
                    </select>

                    <select
                      value={config.package}
                      onChange={(e) =>
                        updateMaxConfiguration(index, "package", e.target.value)
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Paket wählen</option>
                      {PACKAGE_OPTIONS.map((pkg) => (
                        <option key={pkg.value} value={pkg.value}>
                          {pkg.label}
                        </option>
                      ))}
                    </select>

                    <input
                      type="number"
                      min="0"
                      value={config.quantity}
                      onChange={(e) =>
                        updateMaxConfiguration(
                          index,
                          "quantity",
                          parseInt(e.target.value) || 0,
                        )
                      }
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />

                    <button
                      onClick={() => removeMaxConfiguration(index)}
                      disabled={maxConfigurations.length === 1}
                      className={`p-2 rounded-lg transition-colors ${
                        maxConfigurations.length === 1
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-red-600 hover:text-red-800 hover:bg-red-50"
                      }`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                {/* Add Configuration Button */}
                <button
                  onClick={addMaxConfiguration}
                  className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  <span className="text-lg">+</span>
                  Konfiguration hinzufügen
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
              <button
                onClick={() => setMaxConfigModalOpen(false)}
                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleMaxConfigSave}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddProductForm;
